import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { MeResponse } from '../api/protocol';
import { AdsConsentSetting } from '../components/AdSlot';
import { Avatar } from '../components/Avatar';
import { Icon } from '../components/Icon';
import { KeyCapture } from '../components/KeyCapture';
import { BRAND } from '../components/Logo';
import { MicMeter } from '../components/MicMeter';
import { Slider } from '../components/Slider';
import { Toggle } from '../components/Toggle';
import { useDevices, usePrefs, useVoice } from '../hooks';
import { navigate, useLocationKey } from '../router';
import { clearPeerPrefs, listPeerPrefs, savePrefs, type Prefs, type ThemePref } from '../prefs';
import { applyTheme } from '../theme';
import { SETTINGS_CATEGORIES, categoryFromHash, type SettingsCategoryId } from './settingsCategories';
import type { VoiceController } from '../voice/VoiceController';

function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  testId,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  label: string;
  testId?: string;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label} data-testid={testId}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={value === o.value ? 'is-active' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SettingsTabs({ active }: { active: SettingsCategoryId }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  // Keep the active tab visible when the bar scrolls (mobile), e.g. opening /settings#account.
  useEffect(() => {
    const el = refs.current[SETTINGS_CATEGORIES.findIndex((c) => c.id === active)];
    const bar = el?.parentElement;
    if (!el || !bar) return;
    const left = el.offsetLeft - 8;
    const right = el.offsetLeft + el.offsetWidth + 8;
    if (left < bar.scrollLeft) bar.scrollLeft = left;
    else if (right > bar.scrollLeft + bar.clientWidth) bar.scrollLeft = right - bar.clientWidth;
  }, [active]);
  const select = (id: SettingsCategoryId) => navigate(`/settings#${id}`, { replace: true });
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const n = SETTINGS_CATEGORIES.length;
    const next = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    select(SETTINGS_CATEGORIES[next]!.id);
    refs.current[next]?.focus();
  };
  return (
    <nav className="settings-tabs-wrap" aria-label="Categorie delle impostazioni">
      <div className="settings-tabs" role="tablist" aria-label="Categorie">
        {SETTINGS_CATEGORIES.map((c, i) => {
          const on = c.id === active;
          return (
            <button
              key={c.id}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`settings-tab-${c.id}`}
              aria-selected={on}
              aria-controls={`settings-panel-${c.id}`}
              tabIndex={on ? 0 : -1}
              className={`settings-tab${on ? ' is-active' : ''}`}
              data-testid={`settings-tab-${c.id}`}
              onClick={() => select(c.id)}
              onKeyDown={(e) => onKey(e, i)}
            >
              <Icon name={c.icon} size={14} />
              {c.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export function SettingsPage({
  controller,
  me,
  version,
  onLogout,
}: {
  controller: VoiceController;
  me: MeResponse | null;
  version: string | null;
  onLogout: () => Promise<void>;
}) {
  const s = useVoice(controller);
  const prefs = usePrefs();
  const devices = useDevices(s.mic.status);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(() => listPeerPrefs().length);
  const [outputError, setOutputError] = useState(false);

  useLocationKey(); // re-render when the hash (active tab) changes
  const active = categoryFromHash(window.location.hash);

  const ptt = s.mic.mode === 'ptt';
  const hintsHidden = prefs.backgroundHintDismissed || prefs.headphonesHintDismissed;
  // RNNoise non supportato o fallito: il toggle si spegne con una spiegazione (dopo un errore si
  // può riprovare riattivandolo).
  const nsUnavailable = s.noiseSuppression.status === 'unsupported' || (s.noiseSuppression.status === 'error' && prefs.advancedNoiseSuppression);
  const setMode = (m: 'vad' | 'ptt') => {
    savePrefs({ micMode: m });
    controller.setMicMode(m);
  };

  const setTheme = (theme: ThemePref) => {
    savePrefs({ theme });
    applyTheme(theme);
  };

  const panels: Record<SettingsCategoryId, ReactNode> = {
    microfono: (
      <section className="glass card settings-card" id="settings-panel-microfono" role="tabpanel" aria-labelledby="settings-tab-microfono" data-testid="settings-panel-microfono">
        <h2 className="card-title" id="settings-microfono">
          <Icon name="mic" size={14} /> Microfono
        </h2>
        <div className="field">
          <label className="field-label" htmlFor="input-device">
            Dispositivo di ingresso
          </label>
          <select
            id="input-device"
            data-testid="input-device"
            value={prefs.inputDeviceId ?? ''}
            onChange={(e) => {
              const id = e.target.value || null;
              savePrefs({ inputDeviceId: id });
              void controller.setInputDevice(id);
            }}
          >
            <option value="">Predefinito di sistema</option>
            {devices.inputs.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Microfono ${i + 1}`}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <span className="field-label">Livello del microfono</span>
          <MicMeter controller={controller} active={s.mic.transmitting && s.mic.gateOpen} />
          <p className="field-hint">
            {s.entered
              ? s.mic.status === 'ok'
                ? 'Parla: l’indicatore mostra il livello dal vivo.'
                : 'Il microfono non è disponibile: controlla i permessi del browser.'
              : 'Entra in chat vocale per vedere il livello dal vivo del microfono.'}
          </p>
        </div>

        <div className="setting-block">
          <div className="setting-row">
            <div>
              <span className="field-label">Attivazione a voce</span>
              <p className="field-hint">
                {prefs.autoSensitivity
                  ? prefs.speakerMode
                    ? 'Sensibilità automatica, con una soglia minima per la modalità altoparlanti'
                    : 'Sensibilità automatica'
                  : 'Trasmetti solo sopra la soglia'}
              </p>
            </div>
            <Toggle
              checked={prefs.autoSensitivity}
              label="Sensibilità automatica"
              testId="auto-sensitivity"
              onChange={(v) => {
                savePrefs({ autoSensitivity: v });
                controller.setVoiceGate(v, prefs.vadThreshold);
              }}
            />
          </div>
          {!prefs.autoSensitivity ? (
            <div className="threshold">
              <Slider
                min={0}
                max={100}
                value={Math.round(prefs.vadThreshold * 100)}
                onChange={(v) => {
                  savePrefs({ vadThreshold: v / 100 });
                  controller.setVoiceGate(false, v / 100);
                }}
                label="Soglia di attivazione"
                testId="vad-threshold"
              />
              <p className="field-hint">
                Soglia {Math.round(prefs.vadThreshold * 100)}%: parla e regola finché l’indicatore si accende solo con la tua voce.
                {prefs.speakerMode && s.mic.effectiveThreshold !== null && s.mic.effectiveThreshold > prefs.vadThreshold
                  ? ` In modalità altoparlanti la soglia minima è ${Math.round(s.mic.effectiveThreshold * 100)}%.`
                  : ''}
              </p>
            </div>
          ) : null}
        </div>

        <div className="setting-block">
          <div className="setting-row">
            <div>
              <span className="field-label">Push-to-talk</span>
              <p className="field-hint">Funziona solo con questa scheda in primo piano.</p>
            </div>
            <Toggle checked={ptt} label="Push-to-talk" testId="ptt-toggle" onChange={(v) => setMode(v ? 'ptt' : 'vad')} />
          </div>
          {ptt ? (
            <div className="setting-row">
              <span className="small">Tasto</span>
              <KeyCapture value={prefs.pttKey} onChange={(code) => savePrefs({ pttKey: code })} />
            </div>
          ) : null}
        </div>

        <div className="setting-block">
          <div className="setting-row">
            <div>
              <span className="field-label">Riduzione rumore avanzata</span>
              <p className="field-hint" data-testid="noise-suppression-hint">
                {nsUnavailable
                  ? s.noiseSuppression.status === 'unsupported'
                    ? 'Non disponibile su questo browser (servono AudioWorklet e WebAssembly): resta attiva la riduzione rumore del browser.'
                    : 'Non sono riuscito ad avviarla su questo dispositivo: resta attiva la riduzione rumore del browser.'
                  : s.noiseSuppression.status === 'loading'
                    ? 'Avvio della riduzione rumore…'
                    : 'Filtra ventole, tastiera e rumori di fondo; usa più CPU.'}
              </p>
            </div>
            <Toggle
              checked={prefs.advancedNoiseSuppression && !nsUnavailable}
              disabled={s.noiseSuppression.status === 'unsupported'}
              label="Riduzione rumore avanzata"
              testId="noise-suppression-toggle"
              onChange={(v) => {
                savePrefs({ advancedNoiseSuppression: v });
                void controller.setAdvancedNoiseSuppression(v);
              }}
            />
          </div>
        </div>

        <label className="check">
          <input type="checkbox" checked={prefs.startMuted} onChange={(e) => savePrefs({ startMuted: e.target.checked })} />
          <span>Entra con il microfono spento</span>
        </label>
      </section>
    ),
    audio: (
      <section className="glass card settings-card" id="settings-panel-audio" role="tabpanel" aria-labelledby="settings-tab-audio" data-testid="settings-panel-audio">
        <h2 className="card-title" id="settings-audio">
          <Icon name="headphones" size={14} /> Audio
        </h2>
        <div className="field">
          <label className="field-label" htmlFor="output-device">
            Dispositivo di uscita
          </label>
          {devices.outputSupported ? (
            <select
              id="output-device"
              data-testid="output-device"
              value={prefs.outputDeviceId ?? ''}
              onChange={async (e) => {
                const id = e.target.value || null;
                savePrefs({ outputDeviceId: id });
                setOutputError(!(await controller.setOutputDevice(id)));
              }}
            >
              <option value="">Predefinito di sistema</option>
              {devices.outputs
                .filter((d) => d.deviceId !== 'default')
                .map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Uscita ${i + 1}`}
                  </option>
                ))}
            </select>
          ) : (
            <p className="field-hint">Questo browser non permette di scegliere l’uscita audio: usa quella di sistema.</p>
          )}
          {outputError ? <p className="form-error">Non riesco a usare questo dispositivo.</p> : null}
        </div>

        <div className="setting-block">
          <div className="setting-row">
            <div>
              <span className="field-label">Modalità altoparlanti</span>
              <p className="field-hint">
                Attivala se senti gli altri dalle casse invece che dalle cuffie: spegne il guadagno automatico del microfono, alza la soglia minima della voce, abbassa
                le voci mentre parli e controlla di continuo i fischi (effetto Larsen).
              </p>
            </div>
            <Toggle
              checked={prefs.speakerMode}
              label="Modalità altoparlanti"
              testId="speaker-mode-toggle"
              onChange={(v) => {
                savePrefs({ speakerMode: v });
                void controller.setSpeakerMode(v);
              }}
            />
          </div>
        </div>

        <div className="setting-block">
          <div className="setting-row">
            <div>
              <span className="field-label">Abbassa le voci mentre parli</span>
              <p className="field-hint">
                {prefs.speakerMode ? 'Sempre attivo in modalità altoparlanti.' : 'Facoltativo in cuffia: riduce le voci degli altri mentre trasmetti.'}
              </p>
            </div>
            <Toggle
              checked={prefs.speakerMode || prefs.duckInHeadphones}
              disabled={prefs.speakerMode}
              label="Abbassa le voci mentre parli"
              testId="ducking-toggle"
              onChange={(v) => {
                savePrefs({ duckInHeadphones: v });
                controller.setDuckingPrefs(v, prefs.duckAmount);
              }}
            />
          </div>
          {prefs.speakerMode || prefs.duckInHeadphones ? (
            <div className="threshold">
              <Slider
                min={10}
                max={90}
                step={5}
                value={Math.round(prefs.duckAmount * 100)}
                onChange={(v) => {
                  savePrefs({ duckAmount: v / 100 });
                  controller.setDuckingPrefs(prefs.duckInHeadphones, v / 100);
                }}
                label="Riduzione delle voci mentre parli"
                testId="ducking-level"
              />
              <p className="field-hint">Mentre parli le voci scendono del {Math.round(prefs.duckAmount * 100)}%.</p>
            </div>
          ) : null}
        </div>

        <div className="setting-block">
          <div className="setting-row setting-row-wrap">
            <div>
              <span className="field-label" id="audio-output-label">
                Audio
              </span>
              <p className="field-hint">
                {prefs.audioOutput === 'mono' ? 'Mono: stessa voce nelle due orecchie, senza direzione.' : 'Stereo: senti da che parte arriva ogni voce (3D).'}
              </p>
            </div>
            <Segmented<Prefs['audioOutput']>
              label="Audio"
              value={prefs.audioOutput}
              onChange={(v) => {
                savePrefs({ audioOutput: v });
                controller.setMono(v === 'mono');
              }}
              testId="audio-output-mode"
              options={[
                { value: 'stereo', label: 'Stereo' },
                { value: 'mono', label: 'Mono' },
              ]}
            />
          </div>
        </div>

        <div className="setting-block">
          <div className="setting-row">
            <div>
              <span className="field-label">Effetto prossimità</span>
              <p className="field-hint">
                {prefs.proximityEffect
                  ? 'Le voci si abbassano man mano che i giocatori si allontanano.'
                  : `Senti tutti a volume pieno fino a ${s.gain.hearRadius} blocchi, poi la voce sparisce.`}
              </p>
            </div>
            <Toggle
              checked={prefs.proximityEffect}
              label="Effetto prossimità"
              testId="proximity-toggle"
              onChange={(v) => {
                savePrefs({ proximityEffect: v });
                controller.setProximity(v);
              }}
            />
          </div>
        </div>

        <div className="field field-row">
          <div>
            <span className="field-label">Volumi dei giocatori</span>
            <p className="field-hint">{saved === 0 ? 'Nessun giocatore con volume modificato.' : `${saved} giocatori con volume o muto personalizzato.`}</p>
          </div>
          <button
            type="button"
            className="btn btn-small"
            disabled={saved === 0}
            onClick={() => {
              clearPeerPrefs();
              setSaved(0);
            }}
          >
            Ripristina
          </button>
        </div>
      </section>
    ),
    aspetto: (
      <section className="glass card settings-card" id="settings-panel-aspetto" role="tabpanel" aria-labelledby="settings-tab-aspetto" data-testid="settings-panel-aspetto">
        <h2 className="card-title" id="settings-aspetto">
          <Icon name="gear" size={14} /> Aspetto
        </h2>
        <div className="field">
          <span className="field-label">Tema</span>
          <Segmented<ThemePref>
            label="Tema"
            value={prefs.theme}
            onChange={setTheme}
            testId="theme-switch"
            options={[
              { value: 'dark', label: 'Scuro' },
              { value: 'light', label: 'Chiaro' },
              { value: 'system', label: 'Sistema' },
            ]}
          />
        </div>
        <div className="field field-row">
          <div>
            <span className="field-label">Suggerimenti</span>
            <p className="field-hint">
              {hintsHidden ? 'Hai nascosto alcuni suggerimenti della pagina voce.' : 'Tutti i suggerimenti sono visibili.'}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-small"
            disabled={!hintsHidden}
            data-testid="reset-hints"
            onClick={() => savePrefs({ backgroundHintDismissed: false, headphonesHintDismissed: false })}
          >
            Mostra di nuovo i suggerimenti
          </button>
        </div>
        <AdsConsentSetting />
      </section>
    ),
    account: (
      <section className="glass card settings-card" id="settings-panel-account" role="tabpanel" aria-labelledby="settings-tab-account" data-testid="settings-panel-account">
        <h2 className="card-title">
          <Icon name="users" size={14} /> Account
        </h2>
        {me ? (
          <div className="account">
            <Avatar uuid={me.uuid} name={me.name} size={56} />
            <div>
              <p className="account-name">{me.name}</p>
              <p className="muted mono small">{me.uuid}</p>
              <p className="muted small">Collegato dal {new Date(me.linkedAt).toLocaleString('it-IT', { dateStyle: 'long', timeStyle: 'short' })}</p>
            </div>
          </div>
        ) : null}
        <p className="field-hint">
          Scollegando questo browser la voce si chiude qui. Per scollegare tutti i dispositivi usa <kbd>/voice unlink</kbd> in gioco.
        </p>
        {!confirmLogout ? (
          <button type="button" className="btn btn-danger-outline" onClick={() => setConfirmLogout(true)} data-testid="logout-button">
            <Icon name="door" size={16} /> Scollega questo browser
          </button>
        ) : (
          <div className="confirm-row">
            <span>Sicuro? Dovrai ricollegarti con /voice.</span>
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy}
              data-testid="logout-confirm"
              onClick={async () => {
                setBusy(true);
                try {
                  await onLogout();
                } finally {
                  setBusy(false);
                }
              }}
            >
              Scollega
            </button>
            <button type="button" className="btn" onClick={() => setConfirmLogout(false)}>
              Annulla
            </button>
          </div>
        )}
      </section>
    ),
    info: (
      <section className="glass card settings-card" id="settings-panel-info" role="tabpanel" aria-labelledby="settings-tab-info" data-testid="settings-panel-info">
        <h2 className="card-title" id="settings-info">
          <Icon name="info" size={14} /> Informazioni
        </h2>
        <dl className="info-list" data-testid="settings-info">
          <div>
            <dt>Prodotto</dt>
            <dd>{BRAND.product}</dd>
          </div>
          <div>
            <dt>Network</dt>
            <dd>
              <a href={BRAND.url} target="_blank" rel="noopener noreferrer">
                {BRAND.domain}
              </a>
            </dd>
          </div>
          <div>
            <dt>Versione</dt>
            <dd className="mono">{version ? `v${version}` : '…'}</dd>
          </div>
        </dl>
        <p className="muted small">
          {BRAND.product} è la voce di prossimità del network Minecraft Titanet: senti solo chi ti è vicino, con la direzione da cui parla.
          Nessuna registrazione.
        </p>
        <p className="muted small">Non affiliato a Mojang o Microsoft.</p>
      </section>
    ),
  };

  return (
    <main className="settings-page" data-testid="settings-page">
      <header className="page-head">
        <h1 className="page-title">Impostazioni</h1>
        <p className="muted">Le preferenze restano su questo browser e si applicano subito, anche mentre sei in voce.</p>
      </header>

      <SettingsTabs active={active} />
      {panels[active]}
    </main>
  );
}
